import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlterarSenha = ({ userType = 'admin' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const getHeaders = () => {
    const tokenKey = userType === 'admin' ? 'admin_token' : 'parent_token';
    const token = localStorage.getItem(tokenKey);
    return { Authorization: `Bearer ${token}` };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password || !form.confirm_password) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (form.new_password.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);
    try {
      const endpoint = userType === 'admin' ? 'admin/change-password' : 'parent/change-password';
      await axios.put(`${API}/${endpoint}`, {
        current_password: form.current_password,
        new_password: form.new_password
      }, { headers: getHeaders() });

      toast.success('Senha alterada com sucesso!');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao alterar senha';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const backPath = userType === 'admin' ? '/dashboard' : '/app-pais';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F2F7F4] to-[#E8F0EB]">
      <div className="max-w-md mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate(backPath)}
          data-testid="btn-back"
          className="flex items-center gap-2 text-[#1A4D2E] hover:text-[#5A7A3B] mb-6 font-inter font-semibold"
        >
          <ArrowLeft className="w-5 h-5" /> Voltar
        </button>

        <Card className="bg-white rounded-2xl p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-[#1A4D2E]">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-nunito text-xl font-bold text-[#1A4D2E]">Alterar Senha</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Senha Atual</Label>
              <div className="relative">
                <Input
                  data-testid="input-current-password"
                  type={showCurrent ? 'text' : 'password'}
                  value={form.current_password}
                  onChange={(e) => setForm(prev => ({ ...prev, current_password: e.target.value }))}
                  className="rounded-xl pr-10"
                  placeholder="Digite sua senha atual"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Nova Senha</Label>
              <div className="relative">
                <Input
                  data-testid="input-new-password"
                  type={showNew ? 'text' : 'password'}
                  value={form.new_password}
                  onChange={(e) => setForm(prev => ({ ...prev, new_password: e.target.value }))}
                  className="rounded-xl pr-10"
                  placeholder="Mínimo 6 caracteres"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  data-testid="input-confirm-password"
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirm_password}
                  onChange={(e) => setForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                  className="rounded-xl pr-10"
                  placeholder="Repita a nova senha"
                  required
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-confirm-password">
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="btn-change-password"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#2D6B45] text-white rounded-xl py-5 font-bold"
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default AlterarSenha;
